import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

// ─── ResetPassword ──────────────────────────────────────────────────────────
//
// Landing target for the password-reset email link. URL shape Supabase
// uses:
//
//   https://tryposted.app/?view=reset-password#access_token=...&type=recovery
//
// The recovery token lives in the URL fragment (after #), which Supabase's
// JS client picks up automatically on initialization — `getSession()` will
// resolve to the recovery session, and `updateUser({ password })` then
// applies the change without us touching the token directly.
//
// ── Token lifecycle ──────────────────────────────────────────────────────
// The recovery token expires 1 hour after Supabase issues it (default).
// If the operator clicks an old link, updateUser() returns an "expired"
// error — we surface that with a CTA back to ForgotPassword to request a
// fresh link.
//
// On successful update we wipe the URL hash + ?view= param via
// history.replaceState so a back-button or browser-restore doesn't replay
// a now-consumed token, then navigate back to login after a 2s confirm.
//
// ── Validation ───────────────────────────────────────────────────────────
// Client-side:
//   - both fields required
//   - min 8 chars (Supabase default; tighten in dashboard later)
//   - both must match
// Server-side error messages from updateUser() (weak password, expired
// token, etc.) surface directly so the operator sees the actual reason.
const MIN_PASSWORD_LEN = 8;

export default function ResetPassword({ onDone, onRequestNewLink }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  // tokenError surfaces when there's no recovery session at all — usually
  // means the operator landed on /?view=reset-password without a valid
  // link (typed the URL directly, link expired and stripped, etc.).
  const [tokenError, setTokenError] = useState(null);

  // On mount, confirm there's a recovery session. Supabase populates it
  // from the URL fragment automatically before we get here, so getSession
  // is enough to detect "no token" cases without us parsing the URL.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.auth.getSession();
      if (cancelled) return;
      if (error) {
        setTokenError('Could not read your reset link. Request a new one.');
        return;
      }
      // No session at all → operator probably typed the URL or the link
      // is malformed. Surface the same recovery CTA.
      if (!data?.session) {
        setTokenError('This reset link is invalid or has expired. Request a new one.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function validate() {
    if (!newPassword || !confirmPassword) {
      return 'Both fields are required.';
    }
    if (newPassword.length < MIN_PASSWORD_LEN) {
      return `Password must be at least ${MIN_PASSWORD_LEN} characters.`;
    }
    if (newPassword !== confirmPassword) {
      return 'Passwords don\'t match.';
    }
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setLoading(true);
    setError('');
    const { error: updateErr } = await supabase.auth.updateUser({ password: newPassword });
    if (updateErr) {
      // Surface the actual Supabase message — operators benefit from
      // knowing whether it's "expired" vs "weak password" vs network.
      const msg = String(updateErr.message || '');
      if (/expired|invalid/i.test(msg)) {
        setTokenError('This reset link has expired. Request a new one.');
      } else {
        setError(msg || 'Could not update password. Try again.');
      }
      setLoading(false);
      return;
    }
    setSuccess(true);
    setLoading(false);
    // Wipe the URL so a back-button restore doesn't replay the token.
    try {
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (_) { /* ignore */ }
    // Sign the user out so the recovery session doesn't auto-log them in
    // — they should re-authenticate with the new password to confirm it.
    await supabase.auth.signOut();
    setTimeout(() => { onDone?.(); }, 2000);
  }

  if (tokenError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 w-full max-w-sm">
          <div className="text-center mb-6">
            <div className="text-2xl font-medium text-gray-900 mb-1">Link unavailable</div>
            <div className="text-sm text-gray-400 leading-relaxed">{tokenError}</div>
          </div>
          <button
            type="button"
            onClick={onRequestNewLink}
            className="w-full bg-blue-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-blue-700"
          >
            Request a new reset link
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl border border-gray-200 p-8 w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="text-2xl font-medium text-gray-900 mb-1">Set a new password</div>
          <div className="text-sm text-gray-400">Pick something you'll remember this time.</div>
        </div>

        {success ? (
          <div className="text-sm text-gray-700 bg-green-50 border border-green-100 px-4 py-3 rounded-lg">
            Password updated. Redirecting to sign in…
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 block mb-1.5">New password</label>
              <input
                type="password"
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value); if (error) setError(''); }}
                placeholder={`min. ${MIN_PASSWORD_LEN} characters`}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-blue-400 bg-gray-50"
                required
                autoFocus
                autoComplete="new-password"
              />
            </div>
            <div className="mb-6">
              <label className="text-xs font-medium text-gray-500 block mb-1.5">Confirm password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); if (error) setError(''); }}
                placeholder="re-enter password"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-blue-400 bg-gray-50"
                required
                autoComplete="new-password"
              />
            </div>
            {error && (
              <div className="mb-4 text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
